import type { ConditionDto } from "@repo/data-store";
import { Badge } from "@repo/ui/components/ui/badge";
import { Button } from "@repo/ui/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@repo/ui/components/ui/card";
import { cn } from "@repo/ui/lib/utils";
import { Link } from "@tanstack/react-router";
import { Link2, Plus } from "lucide-react";
import { type ReactNode, useState } from "react";

const CLINICAL_STATUS_LABELS: Record<string, string> = {
  active: "Active",
  recurrence: "Recurrence",
  relapse: "Relapse",
  inactive: "Inactive",
  remission: "Remission",
  resolved: "Resolved",
};

const CLINICAL_STATUS_COLORS: Record<string, string> = {
  active: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
  recurrence: "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200",
  relapse: "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200",
  inactive: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300",
  remission: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
  resolved: "bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-200",
};

const CLINICAL_STATUS_BORDERS: Record<string, string> = {
  active: "border-green-400 dark:border-green-700",
  recurrence: "border-amber-400 dark:border-amber-700",
  relapse: "border-amber-400 dark:border-amber-700",
  inactive: "border-gray-300 dark:border-gray-700",
  remission: "border-blue-400 dark:border-blue-700",
  resolved: "border-slate-300 dark:border-slate-700",
};

const VERIFICATION_LABELS: Record<string, string> = {
  unconfirmed: "Unconfirmed",
  provisional: "Provisional",
  differential: "Differential",
  confirmed: "Confirmed",
  refuted: "Refuted",
  "entered-in-error": "Error",
};

const ACTIVE_STATUSES: ReadonlySet<string> = new Set(["active", "recurrence", "relapse"]);

/** True if the condition is in an active clinical status (active/recurrence/relapse). */
export function isActiveStatus(c: ConditionDto): boolean {
  return ACTIVE_STATUSES.has(c.clinicalStatus ?? "active");
}

/** Sorts by onsetDateTime desc; missing onset sinks to the bottom. */
export function sortByOnsetDesc(conditions: ConditionDto[]): ConditionDto[] {
  return [...conditions].sort((a, b) => {
    const ta = a.onsetDateTime ? Date.parse(a.onsetDateTime) : Number.NEGATIVE_INFINITY;
    const tb = b.onsetDateTime ? Date.parse(b.onsetDateTime) : Number.NEGATIVE_INFINITY;
    return tb - ta;
  });
}

function Root({ children }: { children: ReactNode }) {
  return <Card size="sm">{children}</Card>;
}

function Header({ title, action }: { title: string; action?: ReactNode }) {
  return (
    <CardHeader>
      <div className="flex items-center justify-between w-full">
        <CardTitle className="text-sm font-semibold">{title}</CardTitle>
        {action}
      </div>
    </CardHeader>
  );
}

function AddButton({ onClick, title = "Add condition" }: { onClick: () => void; title?: string }) {
  return (
    <Button variant="ghost" size="icon" className="h-6 w-6 -mr-1" onClick={onClick} title={title}>
      <Plus className="h-4 w-4" />
    </Button>
  );
}

function Body({ children }: { children: ReactNode }) {
  return (
    <CardContent>
      <div className="space-y-4">{children}</div>
    </CardContent>
  );
}

function Empty({ children = "No problems recorded." }: { children?: ReactNode }) {
  return <p className="text-xs text-muted-foreground">{children}</p>;
}

function ResolvedSection({ count, children }: { count: number; children: ReactNode }) {
  const [show, setShow] = useState(false);
  if (count === 0) return null;
  return (
    <div className="pt-2 border-t">
      <Button
        variant="link"
        size="xs"
        className="h-auto p-0 text-xs text-muted-foreground"
        onClick={() => setShow(!show)}
      >
        {show ? "Hide" : "Show"} resolved ({count})
      </Button>
      {show && <div className="mt-3 space-y-4">{children}</div>}
    </div>
  );
}

interface ItemProps {
  condition: ConditionDto;
  /** When set, wraps the row in a Link to the case and adds an "On case" pill. */
  linkTo?: { patientId: string; caseId: string };
}

function Item({ condition, linkTo }: ItemProps) {
  const code = condition.code;
  const clinicalStatus = condition.clinicalStatus ?? "active";
  const verificationStatus = condition.verificationStatus ?? "unconfirmed";

  const metaParts: ReactNode[] = [];
  if (code.code) metaParts.push(<span key="code">{code.code}</span>);
  if (condition.onsetDateTime) {
    metaParts.push(<span key="onset">onset {new Date(condition.onsetDateTime).toLocaleDateString()}</span>);
  }
  if (linkTo) {
    metaParts.push(
      <span key="link" className="inline-flex items-center gap-1">
        <Link2 className="h-3 w-3" />
        On case
      </span>,
    );
  }

  const wrapperClass = cn(
    "block min-w-0 border-l-2 pl-2.5 py-0.5",
    CLINICAL_STATUS_BORDERS[clinicalStatus] ?? "border-muted-foreground/30",
    linkTo && "hover:bg-muted/50 rounded-r",
  );

  const inner = (
    <>
      <div className="text-sm font-medium leading-snug">{code.display ?? code.text ?? code.code}</div>
      {metaParts.length > 0 && (
        <div className="flex items-center flex-wrap gap-x-1.5 gap-y-0.5 mt-0.5 text-xs text-muted-foreground">
          {metaParts.map((part, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: order is stable per render
            <span key={i} className="inline-flex items-center">
              {i > 0 && <span className="mr-1.5 text-muted-foreground/50">·</span>}
              {part}
            </span>
          ))}
        </div>
      )}
      <div className="flex items-center flex-wrap gap-1 mt-1.5">
        <Badge variant="outline" className={`text-xs px-1.5 py-0 ${CLINICAL_STATUS_COLORS[clinicalStatus] ?? ""}`}>
          {CLINICAL_STATUS_LABELS[clinicalStatus] ?? clinicalStatus}
        </Badge>
        <Badge variant="outline" className="text-xs px-1.5 py-0">
          {VERIFICATION_LABELS[verificationStatus] ?? verificationStatus}
        </Badge>
      </div>
    </>
  );

  if (linkTo) {
    return (
      <Link to="/patients/$patientId/cases/$caseId" params={linkTo} className={wrapperClass} title="Open case">
        {inner}
      </Link>
    );
  }
  return <div className={wrapperClass}>{inner}</div>;
}

export const ProblemList = Object.assign(Root, {
  Header,
  AddButton,
  Body,
  Empty,
  ResolvedSection,
  Item,
});
